import type { Locale } from '~/composables/useAppI18n'
import { normalizeLocale } from '~/composables/useAppI18n'
import { trackLocaleDiagnostics } from '~/utils/monitoring'

// 陪练账号用户信息类型定义
interface PracticeUser {
  id: number;
  company_id: number;
  username: string;
  real_name?: string | null;
  language?: Locale | null;
  status: string;
  created_at: string;
  updated_at: string;
}

// 登录响应类型
interface LoginResponse {
  id: number;
  company_id: number;
  username: string;
  real_name?: string | null;
  language?: Locale | null;
  status: string;
  created_at: string;
  updated_at: string;
  token: string;
}

// 验证登录状态响应类型
interface VerifyResponse {
  success: boolean;
  message: string;
  account: PracticeUser | null;
  error: string | null;
}

// 陪练资格验证响应类型定义
interface PracticeEligibilityResponse {
  has_ongoing: boolean;
  message: string;
  record_id: number | null;
  thread_id: number | null;
  practice_config_id: number | null;
  role_id?: number | null;
  practice_module_id: number | null;
  practice_module_name: string | null;
  practice_type: string | null;
  usage_type?: string | null;
  status: string | null;
  last_message: string | null;
  practice_start_time: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// 全局响应式状态
const currentUser = ref<PracticeUser | null>(null);
const isAuthenticated = ref(false);
const ongoingPractice = ref<PracticeEligibilityResponse | null>(null);
const isCheckingEligibility = ref(false);
const isVerifying = ref(false);

// LocalStorage 键名
const TOKEN_KEY = 'practice_account_token';
const USER_KEY = 'practice_account_info';

const syncLocaleFromUserPreference = (language?: string | null) => {
  if (!import.meta.client || !language) return
  const { setLocale } = useAppI18n()
  setLocale(normalizeLocale(language))
}

/**
 * 获取存储的 token
 */
const getToken = (): string | null => {
  if (import.meta.client) {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('获取token失败:', error);
      return null;
    }
  }
  return null;
};

/**
 * 保存 token
 */
const saveToken = (token: string): void => {
  if (import.meta.client) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (error) {
      console.error('保存token失败:', error);
    }
  }
};

/**
 * 从 localStorage 加载用户信息
 */
const loadUserFromStorage = (): PracticeUser | null => {
  if (import.meta.client) {
    try {
      const stored = localStorage.getItem(USER_KEY);
      if (stored) {
        const user = JSON.parse(stored) as PracticeUser;
        currentUser.value = user;
        syncLocaleFromUserPreference(user.language);
        trackLocaleDiagnostics('load_user_from_storage');
        return user;
      }
    } catch (error) {
      console.error('加载用户信息失败:', error);
      clearUser();
    }
  }
  return null;
};

/**
 * 保存用户信息到 localStorage
 */
const saveUserInfo = (user: PracticeUser): void => {
  if (import.meta.client) {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      currentUser.value = user;
    } catch (error) {
      console.error('保存用户信息失败:', error);
    }
  }
};

/**
 * 清除用户信息和 token
 */
const clearUser = (): void => {
  if (import.meta.client) {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // 清理其他相关的存储
      localStorage.removeItem('username');
      localStorage.removeItem('isLoggedIn');
    } catch (error) {
      console.error('清除用户信息失败:', error);
    }
  }
  currentUser.value = null;
  isAuthenticated.value = false;
};

/**
 * 保存用户信息（登录成功后调用）
 */
const saveUser = (loginResponse: LoginResponse): void => {
  const { token, ...userInfo } = loginResponse;
  saveToken(token);
  saveUserInfo(userInfo);
  syncLocaleFromUserPreference(userInfo.language);
  trackLocaleDiagnostics('login_success');
  isAuthenticated.value = true;
};

/**
 * 更新用户信息
 */
const updateUser = (data: Partial<PracticeUser>): void => {
  if (currentUser.value) {
    const updatedUser = { ...currentUser.value, ...data };
    saveUserInfo(updatedUser);
  }
};

/**
 * 验证登录状态（验证 token 是否有效）
 */
const verifyToken = async (): Promise<boolean> => {
  const token = getToken();
  if (!token) {
    return false;
  }

  isVerifying.value = true;

  try {
    const { $myFetch } = useNuxtApp();
    const response = await ($myFetch as (url: string, options?: { method?: string; headers?: Record<string, string> }) => Promise<VerifyResponse>)(
      '/api/practice-accounts/verify',
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (response.success && response.account) {
      // token 有效，更新用户信息
      saveUserInfo(response.account);
      syncLocaleFromUserPreference(response.account.language);
      trackLocaleDiagnostics('verify_token_success');
      isAuthenticated.value = true;
      return true;
    } else {
      // token 无效，清除存储
      clearUser();
      return false;
    }
  } catch (error) {
    console.error('验证token失败:', error);
    clearUser();
    return false;
  } finally {
    isVerifying.value = false;
  }
};

/**
 * 刷新 token
 */
const refreshToken = async (): Promise<boolean> => {
  const token = getToken();
  if (!token) {
    return false;
  }

  try {
    const { $myFetch } = useNuxtApp();
    const response = await ($myFetch as (url: string, options?: { method?: string; headers?: Record<string, string> }) => Promise<{ token: string; account: PracticeUser }>)(
      '/api/practice-accounts/refresh-token',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (response.token && response.account) {
      saveToken(response.token);
      saveUserInfo(response.account);
      syncLocaleFromUserPreference(response.account.language);
      trackLocaleDiagnostics('refresh_token_success');
      isAuthenticated.value = true;
      return true;
    }
    return false;
  } catch (error) {
    console.error('刷新token失败:', error);
    return false;
  }
};

interface SubmitFeedbackResponse {
  success: boolean;
  message?: string;
}

interface PracticeLanguageResponse {
  success: boolean;
  language: Locale | null;
  message?: string;
}

/**
 * 提交意见反馈（POST /api/practice/user/feedback）
 */
const submitFeedback = async (feedbackText: string): Promise<{ success: boolean; message?: string; error?: string }> => {
  const { t } = useAppI18n();
  const text = feedbackText.trim();
  if (!text) {
    return { success: false, error: t('feedback.placeholder') };
  }
  if (text.length > 2000) {
    return { success: false, error: t('feedback.placeholder') };
  }

  const body: { feedback_text: string; username?: string; uid?: number } = {
    feedback_text: text
  };
  if (currentUser.value?.id != null) {
    body.uid = currentUser.value.id;
  }
  if (currentUser.value?.username) {
    body.username = currentUser.value.username;
  }

  try {
    const { $myFetch } = useNuxtApp();
    const response = await ($myFetch as (url: string, options?: { method?: string; headers?: Record<string, string>; body?: Record<string, string | number> }) => Promise<SubmitFeedbackResponse>)(
      '/api/practice/user/feedback',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      }
    );

    if (response.success) {
      return { success: true, message: response.message || t('common.feedbackSubmitted') };
    }
    return { success: false, error: response.message || t('common.submitFailed') };
  } catch (error: unknown) {
    console.error('提交意见反馈失败:', error);

    const errorObj = error as {
      data?: { detail?: string; message?: string };
      _data?: { detail?: string; message?: string };
      message?: string;
      status?: number;
      statusCode?: number;
    };

    const status = errorObj?.statusCode ?? errorObj?.status;
    let errorMessage =
      errorObj?._data?.detail
      || errorObj?.data?.detail
      || errorObj?._data?.message
      || errorObj?.data?.message
      || errorObj?.message;

    if (status === 422) {
      errorMessage = errorMessage || t('feedback.placeholder');
    } else if (status === 500) {
      errorMessage = errorMessage || t('common.retryLater');
    }

    return { success: false, error: errorMessage || t('common.retryLater') };
  }
};

/**
 * 修改密码
 */
const changePassword = async (oldPassword: string, newPassword: string): Promise<{ success: boolean; message?: string; error?: string }> => {
  const { t } = useAppI18n();
  const token = getToken();
  if (!token) {
    return { success: false, error: t('common.notLoggedIn') };
  }

  try {
    const { $myFetch } = useNuxtApp();
    const response = await ($myFetch as (url: string, options?: { method?: string; headers?: Record<string, string>; body?: Record<string, string> }) => Promise<{ success: boolean; message?: string; detail?: string }>)(
      '/api/practice-accounts/change-password',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: {
          old_password: oldPassword,
          new_password: newPassword
        }
      }
    );

    if (response.success) {
      return { success: true, message: response.message || t('password.changeSuccess') };
    } else {
      return { success: false, error: response.detail || t('password.changeFailed') };
    }
  } catch (error: unknown) {
    console.error('修改密码失败:', error);
    
    // 提取错误信息，优先使用 detail 字段
    // ofetch 的错误对象中，响应体可能在 _data 或 data 中
    const errorObj = error as { 
      data?: { detail?: string; message?: string }; 
      _data?: { detail?: string; message?: string };
      message?: string;
      status?: number;
      statusCode?: number;
    };
    
    // 优先从 _data.detail 或 data.detail 中提取错误信息（API 返回格式：{detail: "旧密码错误"}）
    const errorMessage = errorObj?._data?.detail 
      || errorObj?.data?.detail
      || errorObj?._data?.message
      || errorObj?.data?.message
      || errorObj?.message
      || t('password.changeFailed');
    
    return { success: false, error: errorMessage };
  }
};

const saveLanguagePreference = async (language: Locale): Promise<{ success: boolean; language?: Locale | null; message?: string; error?: string }> => {
  const { t } = useAppI18n();
  const token = getToken();
  if (!token) {
    return { success: false, error: t('common.notLoggedIn') };
  }

  try {
    const { $myFetch } = useNuxtApp();
    const response = await ($myFetch as (url: string, options?: { method?: string; headers?: Record<string, string>; body?: { language: Locale } }) => Promise<PracticeLanguageResponse>)(
      '/api/practice-accounts/language',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: {
          language
        }
      }
    );

    if (response.success) {
      updateUser({ language: response.language ?? language });
      trackLocaleDiagnostics('language_preference_saved', {
        submitted_language: language,
        saved_language: response.language ?? language,
      });
      return {
        success: true,
        language: response.language ?? language,
        message: response.message
      };
    }

    return {
      success: false,
      error: response.message || t('common.submitFailed')
    };
  } catch (error: unknown) {
    console.error('保存语言偏好失败:', error);

    const errorObj = error as {
      data?: { detail?: string; message?: string };
      _data?: { detail?: string; message?: string };
      message?: string;
    };

    const errorMessage =
      errorObj?._data?.detail
      || errorObj?.data?.detail
      || errorObj?._data?.message
      || errorObj?.data?.message
      || errorObj?.message
      || t('common.retryLater');

    return { success: false, error: errorMessage };
  }
};

/**
 * 登出
 */
const logout = (): void => {
  clearUser();
  // 跳转到首页
  if (import.meta.client) {
    navigateTo('/', { replace: true });
  }
};

/**
 * 检查用户是否已登录（同步检查，基于本地存储）
 */
const checkAuth = (): boolean => {
  const token = getToken();
  if (token && !currentUser.value) {
    loadUserFromStorage();
  }
  return !!(token && isAuthenticated.value);
};

/**
 * 初始化并验证登录状态（异步，会调用验证接口）
 */
const initAuth = async (): Promise<boolean> => {
  const token = getToken();
  if (!token) {
    return false;
  }

  // 先尝试从本地存储加载用户信息
  if (!currentUser.value) {
    loadUserFromStorage();
  }

  // 验证 token 是否有效
  return await verifyToken();
};

/**
 * 验证陪练资格
 * 检查用户是否有正在进行的陪练记录
 */
const checkPracticeEligibility = async (): Promise<PracticeEligibilityResponse | null> => {
  if (!currentUser.value) {
    console.warn('用户未登录，跳过陪练资格验证');
    isCheckingEligibility.value = false;
    return null;
  }

  isCheckingEligibility.value = true;

  try {
    const { $myFetch } = useNuxtApp();
    
    // 构建请求参数
    const params = new URLSearchParams();
    if (currentUser.value.id) {
      params.append('uid', currentUser.value.id.toString());
    } else if (currentUser.value.username) {
      params.append('username', currentUser.value.username);
    }

    const url = `/api/practice/user/eligibility?${params.toString()}`;
    const response = await ($myFetch as (url: string) => Promise<PracticeEligibilityResponse>)(url);
    
    // 保存正在进行的陪练记录
    if (response.has_ongoing) {
      ongoingPractice.value = response;
      console.log('发现正在进行的陪练:', response);
    } else {
      ongoingPractice.value = null;
      console.log('没有正在进行的陪练记录');
    }

    return response;
  } catch (error) {
    console.error('验证陪练资格失败:', error);
    throw error;
  } finally {
    isCheckingEligibility.value = false;
  }
};

/**
 * 清除正在进行的陪练记录
 */
const clearOngoingPractice = (): void => {
  ongoingPractice.value = null;
};

export function usePracticeUser() {
  // 初始化时尝试从 localStorage 加载
  if (import.meta.client && !currentUser.value) {
    loadUserFromStorage();
    // 如果有 token，设置认证状态
    if (getToken()) {
      isAuthenticated.value = true;
    }
  }

  return {
    // 响应式状态
    currentUser: readonly(currentUser),
    isAuthenticated: readonly(isAuthenticated),
    ongoingPractice: readonly(ongoingPractice),
    isCheckingEligibility: readonly(isCheckingEligibility),
    isVerifying: readonly(isVerifying),
    
    // 方法
    saveUser,
    clearUser,
    updateUser,
    logout,
    checkAuth,
    initAuth,
    verifyToken,
    refreshToken,
    changePassword,
    saveLanguagePreference,
    submitFeedback,
    getToken,
    loadUserFromStorage,
    checkPracticeEligibility,
    clearOngoingPractice,
  };
}
